import { OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  SimpleChange,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule, MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { Router, RouterModule, RoutesRecognized } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ClientPath } from '@shared/models/client-path.enum';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { DiningResultsLayout } from '@shared/models/dining-results-layout.enum';
import { ExternalPortal, GlobalNavData } from '@shared/models/global-nav.model';
import { MenuList } from '@shared/models/menu-list';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { PayWithPointsPopupComponent } from '@shared/shared/components/pay-with-points-popup/pay-with-points-popup.component';
import { SitemapHeaderComponent } from '@shared/shared/components/sitemap-header/sitemap-header.component';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { CLIENT_PATH } from '@shared/shared/injection-tokens';
import { IMemberProfile } from '@shared/shared/interfaces/member-profile.interfaces';
import { ISiteSettingsSwitches } from '@shared/shared/interfaces/site-settings.interfaces';
import { ScrollService } from '@shared/shared/services/scroll.service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as fromGlobalStore from '@shared/store';
import * as MemberProfileStore from '@shared/store/member-profile';
import { getMemberProfileFeatureState } from '@shared/store/member-profile';
import { getPoints } from '@shared/store/pay-with-points/pay-with-points.selectors';
import { setDiningLayout } from '@shared/store/shared';
import { UserLogout } from '@shared/store/user';
import { DINING_LAYOUT, RESTAURANT_DETAIL_PAGE, SELECTED_TRAVELERS } from 'app.config';
import { NavigationService } from 'core/services/navigation.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { Observable, Subject, combineLatest, fromEvent } from 'rxjs';
import { delay, distinctUntilChanged, filter, first, map, startWith, takeUntil } from 'rxjs/operators';
import { slideInOut } from '../../../shared/shared/components/expandable-card/animations';
import { LanguageService } from '../../../shared/shared/services/language-service';
import { MobileNavComponent } from '../mobile-nav/mobile-nav.component';

@Component({
  selector: 'global-nav',
  templateUrl: './global-nav.component.html',
  styleUrls: ['./global-nav.component.scss'],
  animations: [slideInOut()],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    TranslateModule,
    RouterModule,
    AccessibleClickDirective,
    MatLegacyButtonModule,
    AsyncPipe,
    MatLegacyMenuModule,
    NgFor,
    CustomDatePipe,
    SitemapHeaderComponent,
    MatIconModule,
    OverlayModule,
    PayWithPointsPopupComponent,
    TourMatMenuModule,
    MobileNavComponent,
  ],
})
export class GlobalNavComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @HostBinding('class.ten-edge-header') public stickyHeaderClass = this.deviceService.browser === 'MS-Edge';
  @ViewChild('desktopNav', { static: true }) public desktopNav: HTMLElement;
  public menuState: string;
  public componentData: GlobalNavData;

  /**
   * Visibility flag for platform provider information.
   */
  public showPlatformProvider = false;
  /**
   * Platform provider information type.
   */
  public platformProviderType = '';
  /**
   * Visibility flag for primary navigation
   * links to top level app feature modules (e.g. Travel).
   */
  public showFeatureNavLinks = false;
  /**
   * A list of app feature modules used
   * to render only required primary navigation items.
   */
  public appFeatures: ISiteSettingsSwitches;
  /**
   * Visibility flag for mobile navigation overlay.
   */
  public showMobileNavOverlay = false;

  public memberPayWithPointsEnabled: boolean;
  public points$ = this.ngrxStore.select(getPoints);
  public displayPayWithPointsNote$: Observable<boolean>;

  private unsubscribe$ = new Subject<void>();
  public siteSettingsLoaded = false;

  public member_profile$: Observable<IMemberProfile> = this.ngrxStore.select(
    MemberProfileStore.getMemberProfileFeatureState
  );

  public customisableVariables: CustomisableVariables;
  public payWithPointsPopupOpened = false;
  @ViewChild(MatMenuTrigger, { static: true }) public matMenu: MatMenuTrigger;
  @Input() public skipLinkUrl = '';

  private pinMenuOpen = false;

  public menuList$: Observable<MenuList[]>;
  public siteLogo$: Observable<string>;
  public externalPortal: ExternalPortal = { link: '', name: '' };
  public clientName: string;
  public showBenefitsNav: boolean;
  public skipLinkPath: string;
  public isAccessHrt: boolean;

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private siteSettingsService: SiteSettingsService,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private overlayContainer: OverlayContainer,
    private elmRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private customisableVariablesService: CustomisableVariablesService,
    private deviceService: DeviceDetectorService,
    private navigationService: NavigationService,
    private elementRef: ElementRef,
    private scrollService: ScrollService,
    @Optional() @Inject(CLIENT_PATH) public clientPath: ClientPath,
    siteConfService: SiteConfigService
  ) {
    super(siteConfService, 'header');

    this.menuList$ = this.navigationService.getNavigationLinks();
    this.siteLogo$ = this.ngrxStore.select(fromGlobalStore.getSpecificCustomAsset, { customAsset: 'site_logo' }).pipe(
      filter((assets) => !!assets),
      map((assets) => assets[0]?.file || this.componentData?.logo)
    );

    combineLatest([this.siteSettingsService.fetchSiteSettings(), this.customisableVariablesService.variables])
      .pipe(
        filter(
          ([siteSettings, customisableVariables]) =>
            siteSettings !== null && Object.keys(customisableVariables).length > 0
        ),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([siteSettings, customisableVariables]) => {
        this.siteSettingsLoaded = true;
        this.appFeatures = siteSettings.switches;

        this.showPlatformProvider = siteSettings.fe_configuration.show_platform_provider_nav;
        this.platformProviderType = siteSettings.fe_configuration.platform_provider_type;
        this.showFeatureNavLinks = !siteSettings.fe_configuration.hide_links_to_app_features;
        this.externalPortal.link = customisableVariables.header_external_portal_hyperlink;
        this.externalPortal.name = customisableVariables.header_external_portal;
        this.customisableVariables = customisableVariables;
        this.clientName = siteSettings.fe_configuration.name;
        this.isAccessHrt = siteSettings.switches.access_to_HTR;

        this.watchNavHeight();
      });

    this.languageService.languageUpdated.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.cdr.detectChanges();
    });

    this.displayPayWithPointsNote$ = this.ngrxStore
      .select(getMemberProfileFeatureState)
      .pipe(map((memberProfile) => !memberProfile.pay_with_points_card));
  }

  public ngOnChanges(changes: { [propertyName: string]: SimpleChange }): void {
    if (changes['skipLinkUrl'] && this.skipLinkUrl) {
      this.skipLinkPath = this.skipLinkUrl;
      const mainHeader = document.querySelector('#desktop-nav');
      if (mainHeader) (mainHeader as HTMLElement).focus();
    }
  }

  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.showBenefitsNav = false;
        this.toggleMobileNavigation(false);
      });

    this.ngrxStore
      .select(getMemberProfileFeatureState)
      .pipe(first((memberProfile) => memberProfile.loaded))
      .subscribe((memberProfile) => {
        this.payWithPointsPopupOpened = memberProfile.show_pay_with_points_overlay;
        this.memberPayWithPointsEnabled = memberProfile.flag_loyalty_enabled;
      });
  }

  public navigateToSkipLinkPathOrFocusOnElem(): void {
    switch (this.router.url) {
      case '/member/details':
      case '/member/activities': {
        this.focusOnTabLinkInMemberModule(this.router.url);
        break;
      }
      default: {
        location.href = this.skipLinkPath;
      }
    }
  }

  private focusOnTabLinkInMemberModule(url: '/member/details' | '/member/activities'): void {
    const tabLink = document.querySelector(`.${url.slice(8)}-link`) as HTMLElement;
    if (tabLink) {
      tabLink.focus();
    }
  }

  /**
   * Toggle the visibility of the mobile
   * version of the app header navigation.
   * @param visible - True if the user wishes to display the side nav
   */
  public toggleMobileNavigation(visible: boolean): void {
    // Allows the hide side bar animation to complete before element is hidden
    const animationDelay = !visible ? 400 : 0;

    setTimeout(() => {
      this.showMobileNavOverlay = visible;
    }, animationDelay);

    setTimeout(() => {
      if (this.languageService.isRtlLanguage()) {
        this.menuState = visible === true ? 'in-rtl' : 'out-rtl';
      } else {
        this.menuState = visible === true ? 'in' : 'out';
      }
    }, 0);

    // adds aria hidden when mobile nav is open to try and stop screen readers and focus from jumping
    const rootNode = document.getElementsByTagName('ten-app-root')[0];
    const desktopNavNode = document.getElementById('desktop-nav');
    if (visible) {
      const navElm = this.elmRef.nativeElement;
      rootNode.childNodes.forEach((child: HTMLElement) => {
        if (child !== navElm && child.setAttribute) {
          child.setAttribute('aria-hidden', 'true');
        }
      });
      if (desktopNavNode) {
        desktopNavNode.setAttribute('aria-hidden', 'true');
      }
    } else {
      rootNode.childNodes.forEach((child: HTMLElement) => {
        if (child && child.removeAttribute) {
          child.removeAttribute('aria-hidden');
        }
      });
      if (desktopNavNode) {
        desktopNavNode.removeAttribute('aria-hidden');
      }
    }
  }

  /**
   * Opens the pin menu on hover and adds event listener to close
   */
  public hoverOpenMenu(): void {
    if (!this.pinMenuOpen) {
      this.matMenu.openMenu();
      this.pinMenuOpen = true;
      this.overlayContainer.getContainerElement().addEventListener('mouseleave', this.matMenuCloseListener.bind(this));
    }
  }

  /**
   * Closes the pin menu on hover out, needed to detect mouse leave for the button
   * @param event
   */
  public hoverCloseMenu(event: MouseEvent): void {
    const newTargetElm: any = event.relatedTarget;
    if (!this.overlayContainer.getContainerElement().contains(newTargetElm)) {
      this.closeMenu();
    }
  }

  /**
   * Listener function which gets added to the element to detect if the new target is a child of the parent
   * @param event
   */
  public matMenuCloseListener(event: MouseEvent): void {
    if (!this.overlayContainer.getContainerElement().contains(<any>event.relatedTarget)) {
      this.closeMenu();
    }
  }

  /**
   * Closes the pin menu
   */
  public closeMenu(): void {
    this.matMenu.closeMenu();
    this.pinMenuOpen = false;
    this.overlayContainer.getContainerElement().removeEventListener('mouseleave', <any>this.matMenuCloseListener);
  }

  /**
   * Log out the current user.
   */
  public logoutUser(): void {
    localStorage.removeItem(SELECTED_TRAVELERS);
    this.ngrxStore.dispatch(new UserLogout());
  }

  public onMenuItemClick(): void {
    this.showBenefitsNav = false;
    localStorage.removeItem(RESTAURANT_DETAIL_PAGE);
    localStorage.removeItem(SELECTED_TRAVELERS);
    localStorage.setItem(DINING_LAYOUT, DiningResultsLayout.Default);
    this.ngrxStore.dispatch(setDiningLayout({ layout: DiningResultsLayout.Default }));
  }

  public onMenuItemMouseOver(id: string): void {
    this.showBenefitsNav = id === 'nav_benefits';
  }

  public onMenuItemMouseLeave(): void {
    this.showBenefitsNav = false;
  }

  public onMenuUserProfileClick(): void {
    localStorage.removeItem(SELECTED_TRAVELERS);
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private watchNavHeight(): void {
    fromEvent(window, 'resize')
      .pipe(
        startWith(''),
        delay(0),
        map(() => this.elementRef.nativeElement.offsetHeight),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((navHeight) => {
        this.scrollService.setOffset(navHeight);
      });
  }

  public navigate(link: string, methodName?: string) {
    if (methodName) {
      this[methodName]();
    }
    this.router.navigate([link]);
  }
}
